/**
 * InvitationSection.js
 * ════════════════════════════════════════════════════════════════
 * Карусель останніх новин порталу на головній сторінці.
 * Раніше — захардкоджений масив MESSAGES, тепер — дані з
 * portal-рівня новин (scope='portal'), закріплені (isPinned)
 * йдуть першими (сортування вже на бекенді).
 *
 * Якщо новин немає — секція не рендериться (return null),
 * як і решта умовних секцій в проекті (Галерея/Команда/Новини
 * на сторінці садочка).
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef } from 'react';
import { newsAPI } from '../kindergartens/detail/components/News/newsAPI';
import NewsDetail, { formatNewsDate } from '../kindergartens/detail/components/News/NewsDetail';
import { constr_uri } from '../../services/apiClient';
import './InvitationSection.css';

// Скоригуй шлях до News/ залежно від того, куди винесеш папку

var CAROUSEL_LIMIT = 5; // скільки останніх новин показуємо в каруселі

// Обрізає HTML body до plain-text прев'ю заданої довжини
function stripToPreview(html, maxLen) {
    if (!html) return '';
    var text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (text.length <= maxLen) return text;
    return text.substring(0, maxLen).trim() + '…';
}

const InvitationSection = () => {
  const [messages, setMessages]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection]     = useState('next');
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused]       = useState(false);
  const [openNewsId, setOpenNewsId]   = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    newsAPI.getPublicList('portal', null, { limit: CAROUSEL_LIMIT })
      .then((result) => {
        var items = (result.items || []).map((item) => ({
          id:    item._id,
          title: item.title,
          date:  formatNewsDate(item.publishAt),
          text:  item.excerpt || stripToPreview(item.body, 220),
          coverSrc: item.coverDocId && item.coverFileId
            ? constr_uri('docfile?doc_id=' + item.coverDocId + '&file_id=' + item.coverFileId)
            : null,
        }));
        setMessages(items);
      })
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, []);

  const handleNavigate = (newIndex, dir) => {
    if (isAnimating || newIndex === currentIndex) return;
    setDirection(dir);
    setIsAnimating(true);
    setCurrentIndex(newIndex);
    setTimeout(() => setIsAnimating(false), 300);
  };

  useEffect(() => {
    if (messages.length <= 1) return;
    if (!isPaused) {
      timerRef.current = setInterval(() => {
        if (!isAnimating) {
          setDirection('next');
          setIsAnimating(true);
          setCurrentIndex((prevIndex) => (prevIndex + 1) % messages.length);
          setTimeout(() => setIsAnimating(false), 300);
        }
      }, 5000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, isAnimating, messages.length]);

  const nextSlide = (e) => {
    e?.preventDefault();
    const nextIndex = currentIndex === messages.length - 1 ? 0 : currentIndex + 1;
    handleNavigate(nextIndex, 'next');
  };

  const prevSlide = (e) => {
    e?.preventDefault();
    const prevIndex = currentIndex === 0 ? messages.length - 1 : currentIndex - 1;
    handleNavigate(prevIndex, 'prev');
  };

  // Поки завантажується або новин немає — нічого не показуємо
  if (loading) return null;
  if (messages.length === 0) return null;

  const currentMessage = messages[currentIndex];

  return (
    <section className="my-5 invitation-section-main">
      <div
        className="invitation-slider-wrapper"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div
          key={currentIndex}
          className={`invitation-alert slide-animation ${direction} ${isAnimating ? 'animating' : ''}`}
          onClick={() => setOpenNewsId(currentMessage.id)}
          style={{ cursor: 'pointer' }}
        >
          <div className="invitation-slide-body">
            {currentMessage.coverSrc && (
              <img
                src={currentMessage.coverSrc}
                alt=""
                className="invitation-slide-image"
              />
            )}
            <div className="invitation-slide-text">
              <strong>{currentMessage.title}</strong>
              <span className="date d-block">{currentMessage.date}</span>
              <p className="mt-3">{currentMessage.text}</p>
            </div>
          </div>
        </div>
      </div>

      {messages.length > 1 && (
        <div className="carousel-navigation">
          <div className="carousel-dots">
            {messages.map((_, index) => (
              <span
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => handleNavigate(index, index > currentIndex ? 'next' : 'prev')}
                style={{ cursor: 'pointer' }}
              ></span>
            ))}
          </div>

          <div className="carousel-arrows">
            <a href="#" className="arrow" onClick={prevSlide}>
              <i className="fa-solid fa-chevron-left"></i>
            </a>
            <a href="#" className="arrow" onClick={nextSlide}>
              <i className="fa-solid fa-chevron-right"></i>
            </a>
          </div>
        </div>
      )}

      {openNewsId && (
        <NewsDetail
          scope="portal"
          newsId={openNewsId}
          onClose={() => setOpenNewsId(null)}
        />
      )}
    </section>
  );
};

export default InvitationSection;